import React, { useEffect, useRef, useState } from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import {
  Container,
  Header,
  Title,
  SearchInput,
  SearchResults,
  SearchResult,
  ResultName,
  ResultDetail as ResultHandle,
  SearchMessage,
} from '../../shared/component/modal/SearchModalLayout';
import deleteIcon from '../../asset/icon/delete.png';
import { searchArtistMember } from '../../shared/api/memberApi';
import type { MemberSearchData } from '../../shared/dto/EntityRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';

const WorkArtistSearchModal: React.FC = () => {
  const { standardModal, clearModal } = useModalStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberSearchData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const latestSearchId = useRef(0);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearModal]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const searchId = ++latestSearchId.current;

    if (!normalizedQuery) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }

    setIsSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchArtistMember(normalizedQuery);
        if (latestSearchId.current !== searchId) return;
        setResults(response.data.members ?? []);
      } catch {
        if (latestSearchId.current !== searchId) return;
        setResults([]);
        setSearchFailed(true);
      } finally {
        if (latestSearchId.current === searchId) setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (latestSearchId.current === searchId) latestSearchId.current += 1;
    };
  }, [query]);

  const handleSelect = (member: MemberSearchData) => {
    standardModal?.handleChange(String(member.aui), member);
    clearModal();
  };

  return (
    <Container>
      <Header>
        <Title>Connect artist account</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => clearModal()}
          aria-label="Close"
        />
      </Header>
      <SearchInput
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search Architrave artists"
        aria-label="Search Architrave artists"
        autoFocus
      />
      <SearchResults>
        {isSearching && <SearchMessage>Searching…</SearchMessage>}
        {!isSearching && searchFailed && <SearchMessage>Search failed.</SearchMessage>}
        {!isSearching && !searchFailed && !query.trim() && (
          <SearchMessage>No results.</SearchMessage>
        )}
        {!isSearching && !searchFailed && query.trim() && results.length === 0 && (
          <SearchMessage>No results.</SearchMessage>
        )}
        {!isSearching && results.map((member) => (
          <SearchResult key={member.aui} type="button" onClick={() => handleSelect(member)}>
            <ResultName>{member.username}</ResultName>
            <ResultHandle>@{member.handle}</ResultHandle>
          </SearchResult>
        ))}
      </SearchResults>
    </Container>
  );
};

export default WorkArtistSearchModal;
